import {html} from 'lit';
import Base from '../base.js';
import styles from './grid-col.css' assert {type: 'css'};

/**
 * @element inv-grid-col
 *
 * @class GridCol
 * @classdesc This is a web component that creates a grid column.
 *
 * @slot - This element has a slot for the content.
 *
 * @cssclass .col - Represents a column layout.
 * @cssclass .col-auto - Represents automatic column width.
 * @cssclass .col-1 - Represents 1 column width.
 * @cssclass .col-2 - Represents 2 column width.
 * @cssclass .col-3 - Represents 3 column width.
 * @cssclass .col-4 - Represents 4 column width.
 * @cssclass .col-5 - Represents 5 column width.
 * @cssclass .col-6 - Represents 6 column width.
 * @cssclass .col-7 - Represents 7 column width.
 * @cssclass .col-8 - Represents 8 column width.
 * @cssclass .col-9 - Represents 9 column width.
 * @cssclass .col-10 - Represents 10 column width.
 * @cssclass .col-11 - Represents 11 column width.
 * @cssclass .col-12 - Represents 12 column width.
 * @cssclass .col-sm-auto - Represents automatic column width for small screens.
 * @cssclass .col-sm-1 - Represents 1 column width for small screens.
 * @cssclass .col-sm-2 - Represents 2 column width for small screens.
 * @cssclass .col-sm-3 - Represents 3 column width for small screens.
 * @cssclass .col-sm-4 - Represents 4 column width for small screens.
 * @cssclass .col-sm-5 - Represents 5 column width for small screens.
 * @cssclass .col-sm-6 - Represents 6 column width for small screens.
 * @cssclass .col-sm-7 - Represents 7 column width for small screens.
 * @cssclass .col-sm-8 - Represents 8 column width for small screens.
 * @cssclass .col-sm-9 - Represents 9 column width for small screens.
 * @cssclass .col-sm-10 - Represents 10 column width for small screens.
 * @cssclass .col-sm-11 - Represents 11 column width for small screens.
 * @cssclass .col-sm-12 - Represents 12 column width for small screens.
 * @cssclass .col-md-auto - Represents automatic column width for medium screens.
 * @cssclass .col-md-1 - Represents 1 column width for medium screens.
 * @cssclass .col-md-2 - Represents 2 column width for medium screens.
 * @cssclass .col-md-3 - Represents 3 column width for medium screens.
 * @cssclass .col-md-4 - Represents 4 column width for medium screens.
 * @cssclass .col-md-5 - Represents 5 column width for medium screens.
 * @cssclass .col-md-6 - Represents 6 column width for medium screens.
 * @cssclass .col-md-7 - Represents 7 column width for medium screens.
 * @cssclass .col-md-8 - Represents 8 column width for medium screens.
 * @cssclass .col-md-9 - Represents 9 column width for medium screens.
 * @cssclass .col-md-10 - Represents 10 column width for medium screens.
 * @cssclass .col-md-11 - Represents 11 column width for medium screens.
 * @cssclass .col-md-12 - Represents 12 column width for medium screens.
 * @cssclass .col-lg-auto - Represents automatic column width for large screens.
 * @cssclass .col-lg-1 - Represents 1 column width for large screens.
 * @cssclass .col-lg-2 - Represents 2 column width for large screens.
 * @cssclass .col-lg-3 - Represents 3 column width for large screens.
 * @cssclass .col-lg-4 - Represents 4 column width for large screens.
 * @cssclass .col-lg-5 - Represents 5 column width for large screens.
 * @cssclass .col-lg-6 - Represents 6 column width for large screens.
 * @cssclass .col-lg-7 - Represents 7 column width for large screens.
 * @cssclass .col-lg-8 - Represents 8 column width for large screens.
 * @cssclass .col-lg-9 - Represents 9 column width for large screens.
 * @cssclass .col-lg-10 - Represents 10 column width for large screens.
 * @cssclass .col-lg-11 - Represents 11 column width for large screens.
 * @cssclass .col-lg-12 - Represents 12 column width for large screens.
 * @cssclass .col-xl-auto - Represents automatic column width for extra-large screens.
 * @cssclass .col-xl-1 - Represents 1 column width for extra-large screens.
 * @cssclass .col-xl-2 - Represents 2 column width for extra-large screens.
 * @cssclass .col-xl-3 - Represents 3 column width for extra-large screens.
 * @cssclass .col-xl-4 - Represents 4 column width for extra-large screens.
 * @cssclass .col-xl-5 - Represents 5 column width for extra-large screens.
 * @cssclass .col-xl-6 - Represents 6 column width for extra-large screens.
 * @cssclass .col-xl-7 - Represents 7 column width for extra-large screens.
 * @cssclass .col-xl-8 - Represents 8 column width for extra-large screens.
 * @cssclass .col-xl-9 - Represents 9 column width for extra-large screens.
 * @cssclass .col-xl-10 - Represents 10 column width for extra-large screens.
 * @cssclass .col-xl-11 - Represents 11 column width for extra-large screens.
 * @cssclass .col-xl-12 - Represents 12 column width for extra-large screens.
 * @cssclass .col-xxl-auto - Represents automatic column width for extra-extra-large screens.
 * @cssclass .col-xxl-1 - Represents 1 column width for extra-extra-large screens.
 * @cssclass .col-xxl-2 - Represents 2 column width for extra-extra-large screens.
 * @cssclass .col-xxl-3 - Represents 3 column width for extra-extra-large screens.
 * @cssclass .col-xxl-4 - Represents 4 column width for extra-extra-large screens.
 * @cssclass .col-xxl-5 - Represents 5 column width for extra-extra-large screens.
 * @cssclass .col-xxl-6 - Represents 6 column width for extra-extra-large screens.
 * @cssclass .col-xxl-7 - Represents 7 column width for extra-extra-large screens.
 * @cssclass .col-xxl-8 - Represents 8 column width for extra-extra-large screens.
 * @cssclass .col-xxl-9 - Represents 9 column width for extra-extra-large screens.
 * @cssclass .col-xxl-10 - Represents 10 column width for extra-extra-large screens.
 * @cssclass .col-xxl-11 - Represents 11 column width for extra-extra-large screens.
 * @cssclass .col-xxl-12 - Represents 12 column width for extra-extra-large screens.
 *
 * @cssproperty --inv-grid-gutter-x
 * @description The horizontal space between columns.
 * @type {string}
 * @default '1.5em'
 *
 * @cssproperty --inv-grid-gutter-y
 * @description The vertical space between rows.
 * @type {string}
 * @default '0'
 **/
export default class GridCol extends Base {
    getStyleSheets() {
        return [...super.getStyleSheets(), styles];
    }

    render() {
        return html`
            <slot></slot>
        `;
    }
}

GridCol.register();
